import { useQuery } from '@tanstack/react-query';
import { idAccessToken, UserKeys } from '../api/users-api';
import { useState } from 'react';
import { CallbackDoc } from 'react-google-drive-picker/dist/typeDefs';
import '@googleworkspace/drive-picker-element';
import {
  DrivePicker,
  DrivePickerDocsView,
} from '@googleworkspace/drive-picker-react';
import { PickerPickedEvent } from '@googleworkspace/drive-picker-element';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Grow from '@mui/material/Grow';
import GlobalStyles from '@mui/material/GlobalStyles';
import Description from '@mui/icons-material/Description';
import Folder from '@mui/icons-material/Folder';

const PICKER_CONFIGS = {
  spreadsheet: {
    label: 'Select File',
    viewId: 'SPREADSHEETS',
    mimeType: 'application/vnd.google-apps.spreadsheet',
  },
  folder: {
    label: 'Select Folder',
    viewId: 'FOLDERS',
    mimeType: 'application/vnd.google-apps.folder',
    setSelectFolderEnabled: true,
  },
} as const;

export interface SharedFilePickerProps {
  // Required props
  pickerType: 'spreadsheet' | 'folder';
  selectedFileId: string;
  selectedFileName: string;
  onFileSelected: (file: CallbackDoc) => void;
  onFileRemoved: () => void;

  // Optional props
  maxWidth?: string;
  errorMessage?: string;
  includeSharedDrives?: boolean;
  setParentFolder?: string;
  onBeforeOpen?: () => Promise<boolean> | boolean;
  onPermissionDenied?: () => void;
  multiselect?: boolean;
  showUploadFolders?: boolean;
  showUploadView?: boolean;
  disabled?: boolean;
  customTrigger?: (openPicker: () => void) => React.ReactNode;
}

export default function SharedFilePicker({
  pickerType,
  selectedFileId,
  selectedFileName,
  onFileSelected,
  onFileRemoved,
  maxWidth,
  errorMessage,
  setParentFolder,
  onBeforeOpen,
  onPermissionDenied,
  includeSharedDrives = false,
  multiselect = false,
  disabled = false,
  customTrigger,
}: SharedFilePickerProps) {
  const [mounted, setMounted] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const { data: accessToken } = useQuery({
    queryKey: [UserKeys.AccessToken],
    queryFn: () => idAccessToken(),
    staleTime: Infinity,
  });

  const config = PICKER_CONFIGS[pickerType];
  const hasFileSelected = Boolean(selectedFileId) && !disabled;
  const StartIcon = pickerType === 'folder' ? Folder : Description;
  const url =
    selectedFileId !== ''
      ? pickerType === 'spreadsheet'
        ? `https://docs.google.com/spreadsheets/d/${selectedFileId}`
        : `https://drive.google.com/drive/folders/${selectedFileId}`
      : '';

  const handleOpenPicker = async () => {
    if (disabled || !accessToken) return;

    if (onBeforeOpen) {
      const allowed = await onBeforeOpen();
      if (!allowed) {
        onPermissionDenied?.();
        return;
      }
    }

    setPickerKey((prev) => prev + 1);
    setMounted(true);
  };

  const handlePicked = (event: PickerPickedEvent) => {
    const { docs } = event.detail;

    if (docs && docs.length > 0) {
      onFileSelected(event.detail.docs[0]);
    }
    setMounted(false);
  };

  return (
    <Box>
      {/*
        This GlobalStyles overrides the library's styles to ensure the picker always appears on top.
        See issue: https://issuetracker.google.com/issues/111565589
      */}
      <GlobalStyles
        styles={{
          '.picker-dialog': {
            zIndex: '2200 !important',
          },
          '.picker-dialog-bg': {
            zIndex: '2199 !important',
          },
        }}
      />
      {customTrigger ? (
        customTrigger(handleOpenPicker)
      ) : (
        <Stack
          gap={2}
          flexDirection="row"
          alignItems="center"
          sx={{ mb: 1, maxWidth: maxWidth || 600 }}
        >
          <Button
            variant="contained"
            onClick={handleOpenPicker}
            startIcon={<StartIcon />}
            loading={!accessToken}
            disabled={disabled}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {config.label}
          </Button>
          {hasFileSelected && (
            <Tooltip title={selectedFileName} arrow>
              <Chip
                avatar={<StartIcon />}
                label={selectedFileName}
                onDelete={(e) => {
                  e.preventDefault();
                  onFileRemoved();
                }}
                clickable={url !== ''}
                component={url !== '' ? 'a' : 'div'}
                href={url !== '' ? url : undefined}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flex: 1,
                  maxWidth: 'fit-content',
                  minWidth: 0,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </Tooltip>
          )}
        </Stack>
      )}
      {mounted && (
        <div key={pickerKey}>
          <DrivePicker
            oauth-token={accessToken}
            client-id={import.meta.env.VITE_DRIVE_PICKER_CLIENT_ID}
            developer-key={import.meta.env.VITE_DRIVE_PICKER_API_KEY}
            onPicked={handlePicked}
            onCanceled={() => setMounted(false)}
            multiselect={multiselect}
            parent-folder={setParentFolder}
            enable-drives={includeSharedDrives ? 'true' : 'false'}
          >
            <DrivePickerDocsView
              view-id={config.viewId}
              mime-types={config.mimeType}
              include-folders={config.viewId === 'FOLDERS' ? 'true' : 'false'}
              select-folder-enabled={
                config.viewId === 'FOLDERS' ? 'true' : 'false'
              }
            />
          </DrivePicker>
        </div>
      )}
      {errorMessage && (
        <Grow in={true}>
          <Alert severity="error" sx={{ width: 'fit-content' }}>
            {errorMessage}
          </Alert>
        </Grow>
      )}
    </Box>
  );
}
